import { makeStyles, shorthands, tokens, Button, Tooltip, Text, Persona } from '@fluentui/react-components';
import { FC } from 'react';
import { Settings24Regular, ChatAdd24Regular, Clover24Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { ExploreGptRoute, SettingsRoute } from '../../Routes';
import { IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import AppLogoWithTitle from '../Common/AppLogoWithTitle';
import { useGptConfigurations } from '../../store/hooks';
import { appConfig } from '../../AppConfig';
import MenuCommandsCollapsed from './MenuCommandsCollapsed';
import { generateInitials } from '../../Utils/personaUtils';

const useStyles = makeStyles({
    collapsedContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '50px', // Even narrower width for collapsed state
        height: '100%',
        position: 'absolute', // Position absolutely to ensure it's in the visible area
        right: '0', // Position at the right edge (visible part)
        top: '0',
        ...shorthands.padding(tokens.spacingVerticalS, '0'),
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    },
    collapsedLogo: {
        marginTop: '28px', 
        marginBottom: '20px'
    },
    collapsedIconButton: {
        width: '36px',
        height: '36px',
        minWidth: '36px',
        marginBottom: '15px',
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: tokens.borderRadiusCircular,
        color: tokens.colorNeutralForeground2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorBrandForeground1,
        },
        ':active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
        },
    },
    collapsedIconsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: '49px',
        gap: tokens.spacingVerticalXS,
        width: '100%',
    },
    collapsedBottomIcons: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: tokens.spacingVerticalXS,
        width: '100%',
        marginBottom: tokens.spacingVerticalS,
    },
    collapsedPersona: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '36px',
        marginTop: '17px',
        marginBottom: '25px',
        marginLeft: '10px'
    },
});

interface AppMenuCollapsedProps {
    onNewChat: (gptConfiguration: IGptConfiguration) => void;
    userName?: string;
    userPhoto?: string;
}

const getEnvironmentLabel = (): string | undefined => {
    switch (appConfig.ENVIRONMENT) {
        case 'local':
            return 'Local';
        case 'development':
            return 'Dev';
        case 'test':
            return 'Test';
        case 'preprod':
            return 'Pre-Prod';
        default:
            return undefined;
    }
};

const AppMenuCollapsed: FC<AppMenuCollapsedProps> = ({ onNewChat, userName, userPhoto }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { gptConfigurations } = useGptConfigurations();
    const environmentLabel = getEnvironmentLabel();
    const initials = generateInitials(userName);

    const handleNewChat = () => {
        // Get the default GPT configuration
        const defaultGpt = gptConfigurations.find(gpt => gpt.isDefault) || gptConfigurations[0];
        if (defaultGpt) {
            onNewChat(defaultGpt);
        }
    };

    return (
        <div className={classes.collapsedContent}>
            <div className={classes.collapsedLogo}>
                <AppLogoWithTitle theme='light' collapsed={true} />
            </div>

            <div>
            {environmentLabel && <Text size={200} weight="semibold">{environmentLabel}</Text>}
            </div>

            <div style={{ marginTop: '30px' }}>
                <MenuCommandsCollapsed onNewChat={onNewChat} showOnlyToggleMenuCommand={false} />
            </div>

            {/* Spacer to push bottom icons down */}
            <div style={{ flex: 1 }} />
            
            {/* Settings and Explore GPTs at the bottom */}
            <div className={classes.collapsedBottomIcons}>
                <Tooltip content="Explore GPTs" relationship="label" positioning="after">
                    <Button
                        appearance="subtle"
                        className={classes.collapsedIconButton}
                        icon={<Clover24Regular />}
                        onClick={() => navigate(ExploreGptRoute)}
                    />
                </Tooltip>
                
                <Tooltip content="Settings" relationship="label" positioning="after">
                    <Button
                        appearance="subtle"
                        className={classes.collapsedIconButton}
                        icon={<Settings24Regular />}
                        onClick={() => navigate(SettingsRoute)}
                    />
                </Tooltip>
                
                {/* User Persona at the bottom */}
                {(userName || userPhoto) && (
                    <div className={classes.collapsedPersona}>
                        <Persona
                            size="small"
                            avatar={{
                                image: userPhoto ? { src: userPhoto } : undefined,
                                initials: { 
                                    children: initials,
                                    style: {
                                        color: '#FF6542',
                                        fontWeight: 'bold',
                                        fontSize: '12px'
                                    }
                                },
                                style: {
                                    border: '2px solid #FF6542',
                                    backgroundColor: 'transparent',
                                    width: '32px',
                                    height: '32px'
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppMenuCollapsed;
